import React, { FC } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { StyledFilteredItemChip } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import { IUnit, ProductFilters } from 'src/generated-types'
import { IMaterialFactor } from 'src/generated-types/material-factor'
import { IProductCategoryDropDown } from 'src/api/product-category'

interface FilterChipsProps {
  headerFilters: ProductFilters
  clearFilter: (field: string) => void
  updateFilter: (field: string, value: string | number | number[] | string[]) => void
  units?: IUnit[] | undefined
  categories?: IProductCategoryDropDown[] | undefined
  materialFactors?: IMaterialFactor[] | undefined
}

const ProductFilterChips: FC<FilterChipsProps> = ({
  headerFilters,
  clearFilter,
  updateFilter,
  units,
  categories,
  materialFactors,
}) => {
  const { t: translate } = useTranslation(['product', 'admin-sidebar', 'common'])

  const renderUnitLabelMap = abbr => {
    if (!units) return abbr
    const unit = units.find(u => u.abbreviation === abbr)
    return unit ? `${unit.name} (${unit.abbreviation})` : abbr
  }
  const renderCategoryLabelMap = id => {
    if (!categories) return ''
    const category = categories.find(u => u.id === parseInt(id))
    return category ? category.name : ''
  }
  const renderMaterialFactorLabelMap = id => {
    if (!materialFactors) return ''
    const factor = materialFactors.find(f => f.id === parseInt(id))
    return factor ? factor.description : ''
  }

  const renderFilterChips = () => {
    const chips: React.ReactNode[] = []

    if (headerFilters.units && Array.isArray(headerFilters.units) && headerFilters.units.length > 0) {
      headerFilters.units.forEach((whereValue, index) => {
        chips.push(
          <StyledFilteredItemChip
            key={`unit-${index}-${whereValue}`}
            label={`${translate('unit', { ns: 'product' })}: ${renderUnitLabelMap(whereValue)}`}
            onDelete={() => {
              const newWhere = headerFilters.units?.filter(w => w !== whereValue) || []
              if (newWhere.length === 0) {
                clearFilter('units')
              } else {
                updateFilter('units', newWhere)
              }
            }}
          />,
        )
      })
    }
    if (
      headerFilters.productCategories &&
      Array.isArray(headerFilters.productCategories) &&
      headerFilters.productCategories.length > 0
    ) {
      headerFilters.productCategories.forEach((whereValue, index) => {
        chips.push(
          <StyledFilteredItemChip
            key={`category-${index}-${whereValue}`}
            label={`${translate('category', { ns: 'common' })}: ${renderCategoryLabelMap(whereValue)}`}
            onDelete={() => {
              const newWhere = headerFilters.productCategories?.filter(w => w !== whereValue) || []
              if (newWhere.length === 0) {
                clearFilter('productCategories')
              } else {
                updateFilter('productCategories', newWhere)
              }
            }}
          />,
        )
      })
    }
    if (
      headerFilters.materialFactors &&
      Array.isArray(headerFilters.materialFactors) &&
      headerFilters.materialFactors.length > 0
    ) {
      headerFilters.materialFactors.forEach((whereValue, index) => {
        chips.push(
          <StyledFilteredItemChip
            key={`materialFactor-${index}-${whereValue}`}
            label={`${translate('material-factor', { ns: 'admin-sidebar' })}: ${renderMaterialFactorLabelMap(
              whereValue,
            )}`}
            onDelete={() => {
              const newWhere = headerFilters.materialFactors?.filter(w => w !== whereValue) || []
              if (newWhere.length === 0) {
                clearFilter('materialFactors')
              } else {
                updateFilter('materialFactors', newWhere)
              }
            }}
          />,
        )
      })
    }

    return chips
  }

  const filterChips = renderFilterChips()

  if (filterChips.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{filterChips}</Box>
    </Box>
  )
}

export default ProductFilterChips
